/**
 * reconciliationMoney — the ONE money-fence + presentation-logic home for the property
 * line-item reconciliation surfaces (Slice-1 summary card + the matching workbench).
 *
 * MONEY-FENCE (hard rule, mirrors backend `matching_bridge._line_snapshot`):
 *   Every dollar the UI shows is an engine-emitted string rendered VERBATIM via
 *   `FinancialValue`. The ONLY client-side money arithmetic permitted anywhere in the
 *   reconciliation UI is the per-pair delta / per-side subtotal, done here in INTEGER
 *   CENTS on those strings. `net` / `gross` are rendered verbatim from the summary — never
 *   summed here. Nothing in this file derives a percentage OF a dollar figure.
 *
 * Everything else in this module (humanize / tokenize / word-diff / keywords) is
 * pure NON-money presentation — it never touches `extended_price` as a number.
 */

// ── Money: integer-cents arithmetic only ─────────────────────────────────────
// Returns null (NOT 0) when a value is absent or unparseable, so a bad engine string
// can NEVER be silently rendered as a "verified zero". Null propagates through the
// subtotal / delta and surfaces as FinancialValue's NOT RUN chip.
export function toCents(str: string | number | null | undefined): number | null {
  if (str == null || str === '') return null;
  const cleaned = typeof str === 'string' ? str.replace(/[$,\s]/g, '') : String(str);
  // A string that is ALL currency punctuation ("$", " ", ",", "$ ,") strips to "" and
  // Number("") is 0 — which would silently render an invented "verified zero" for an
  // unparseable engine value. Guard it explicitly (mirrors financialFormat's `cleaned !== ''`
  // guard so the two money parsers cannot drift). Fails to null → FinancialValue "NOT RUN".
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

export interface HasExtendedPrice {
  extended_price: string | number | null | undefined;
}

// Any unparseable line poisons the subtotal — return null rather than under-count.
export function sumCents(lines: HasExtendedPrice[]): number | null {
  let total = 0;
  for (const l of lines) {
    const c = toCents(l.extended_price);
    if (c === null) return null;
    total += c;
  }
  return total;
}

export function pairDeltaCents(
  aLines: HasExtendedPrice[],
  bLines: HasExtendedPrice[],
): number | null {
  const a = sumCents(aLines);
  const b = sumCents(bLines);
  if (a === null || b === null) return null;
  return a - b;
}

// null-safe cents→dollars: keeps null as null (→ FinancialValue NOT RUN) instead of
// letting `null / 100` coerce to 0 (→ a false "verified zero").
export function centsToDollars(cents: number | null): number | null {
  return cents === null ? null : cents / 100;
}

// ── Location humanization (snake_case → Title Case, presentation-only) ────────
// Raw hierarchy values are snake_case ("main_dwelling", "level_1", "living_room").
// Filtering ALWAYS uses the raw value; this is display only.
export function humanizeLoc(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '';
  return String(raw)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// ── Tokenization + word-level diff (the name-mismatch legibility feature) ─────
const STOPWORDS = new Set([
  'the', 'and', 'or', 'a', 'an', 'of', 'to', 'in', 'on', 'at', 'by', 'for', 'with',
  'per', 'two', 'one',
]);

// Normalize a token for comparison/keyword purposes (lowercase, strip non-alphanumerics).
export function normToken(t: string): string {
  return t.toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Split a description into render tokens, preserving the original spelling + spacing group.
function splitTokens(s: string): string[] {
  return (s || '').split(/(\s+)/).filter((t) => t.length > 0);
}

export type DiffSpan = { text: string; kind: 'shared' | 'diff' | 'space' };

// Word-level diff: a token is "shared" if its normalized form appears on the other side.
// Symmetric — each side tints its OWN differing words; neither side is "the wrong one".
// Whitespace tokens are passed through as 'space' so the original spacing renders intact.
export function wordDiff(a: string, b: string): { a: DiffSpan[]; b: DiffSpan[] } {
  const aTokens = splitTokens(a);
  const bTokens = splitTokens(b);
  const aNorm = new Set(aTokens.map(normToken).filter(Boolean));
  const bNorm = new Set(bTokens.map(normToken).filter(Boolean));
  const toSpans = (tokens: string[], otherNorm: Set<string>): DiffSpan[] =>
    tokens.map((t) => {
      if (/^\s+$/.test(t)) return { text: t, kind: 'space' as const };
      const n = normToken(t);
      if (!n) return { text: t, kind: 'shared' as const }; // pure punctuation → neutral
      return { text: t, kind: otherNorm.has(n) ? ('shared' as const) : ('diff' as const) };
    });
  return { a: toSpans(aTokens, bNorm), b: toSpans(bTokens, aNorm) };
}

// Shared keywords between two descriptions — meaningful tokens only (stopwords, pure
// numerics, and 1-char tokens removed). Order follows side A; de-duplicated.
export function sharedKeywords(a: string, b: string, limit = 4): string[] {
  const bNorm = new Set(splitTokens(b).map(normToken).filter(Boolean));
  const out: string[] = [];
  const seen = new Set<string>();
  for (const t of splitTokens(a)) {
    if (/^\s+$/.test(t)) continue;
    const n = normToken(t);
    if (!n || n.length < 2 || seen.has(n)) continue;
    if (STOPWORDS.has(n) || /^\d+$/.test(n)) continue;
    if (bNorm.has(n)) {
      out.push(t.trim());
      seen.add(n);
      if (out.length >= limit) break;
    }
  }
  return out;
}

// Top keyword frequencies across a set of descriptions, for the clickable filter chips.
// Excludes stopwords, pure numerics, short tokens, and any token in `exclude` (the
// hierarchy-level names — redundant with the breadcrumb).
export function topKeywords(
  descriptions: string[],
  exclude: Set<string>,
  limit = 6,
): Array<{ token: string; count: number }> {
  const freq = new Map<string, { token: string; count: number }>();
  for (const desc of descriptions) {
    const seenInDesc = new Set<string>();
    for (const t of splitTokens(desc)) {
      const n = normToken(t);
      if (!n || n.length < 3 || STOPWORDS.has(n) || /^\d+$/.test(n)) continue;
      if (exclude.has(n)) continue;
      if (seenInDesc.has(n)) continue; // count each description once per token
      seenInDesc.add(n);
      const prev = freq.get(n);
      if (prev) prev.count += 1;
      // Display token: strip surrounding punctuation ("cabinet," → "cabinet") while dedup/
      // count stays keyed on the normalized form.
      else freq.set(n, { token: t.trim().toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, ''), count: 1 });
    }
  }
  return Array.from(freq.values())
    .filter((e) => e.count > 1)
    .sort((x, y) => y.count - x.count || x.token.localeCompare(y.token))
    .slice(0, limit);
}
